import FormRow from 'Components/FormRow';
import InputHandler from 'Components/InputHandler';
import Unit from 'Components/Unit';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import { filterByDocumentType } from 'Utility/documentHandling';
import { isEnded } from '../../../../Treatment/utils';

import type { TRelevantDocumentType } from '../../../utils';

type TFieldConfig = (
  | {
      type: 'PartialDate';
      name: string;
      title?: string | undefined;
      titleDescription?: React.JSX.Element | string | undefined;
      dateDefault?: 'now' | PartialDate | undefined;
      isNotCancellable?: boolean;
    }
  | {
      type: 'NumberField';
      name: string;
      title?: string | undefined;
      titleDescription?: React.JSX.Element | string | undefined;
      precision?: number;
      min?: number;
      max?: number;
      placeholder?: string;
      unit?: string | React.JSX.Element;
    }
  | {
      type: 'Radio';
      name: string;
      title?: string | undefined;
      titleDescription?: string | React.JSX.Element | undefined;
      options: Array<string | number>;
      optFormatter?: (name: string | number) => React.JSX.Element;
    }
)[];

const fieldConfig: TFieldConfig = [
  {
    type: 'PartialDate',
    name: 'date',
    title: 'clpAndCnpTests.date',
    titleDescription: undefined,
    isNotCancellable: true,
  },
  {
    type: 'Radio',
    name: 'madTherapyInUse',
    title: 'clpAndCnpTests.madTherapyInUse',
    options: ['yes', 'no', 'unknown'],
    optFormatter: (name: string | number): React.JSX.Element => (
      <FormattedMessage id={`clpAndCnpTests.opts.sleepPolygraphy.${name}`} />
    ),
  },
  {
    type: 'NumberField',
    name: 'ahi',
    title: 'clpAndCnpTests.ahi',
    titleDescription: <FormattedMessage id="clpAndCnpTests.ahiDescription" />,
    precision: 2,
    min: 0,
    max: 999999,
    placeholder: 'clpAndCnpTests.ahi',
    unit: <FormattedMessage id="clpAndCnpTests.perHour" />,
  },
  {
    type: 'NumberField',
    name: 'ahiSupine',
    title: 'clpAndCnpTests.ahiSupine',
    precision: 2,
    min: 0,
    max: 999999,
    placeholder: 'clpAndCnpTests.ahiSupine',
    unit: <FormattedMessage id="clpAndCnpTests.perHour" />,
  },
  {
    type: 'NumberField',
    name: 'rei',
    title: 'clpAndCnpTests.rei',
    titleDescription: <FormattedMessage id="clpAndCnpTests.reiDescription" />,
    precision: 2,
    min: 0,
    max: 999999,
    placeholder: 'clpAndCnpTests.rei',
    unit: <FormattedMessage id="clpAndCnpTests.perHour" />,
  },
  {
    type: 'NumberField',
    name: 'reiSupine',
    title: 'clpAndCnpTests.reiSupine',
    precision: 2,
    min: 0,
    max: 999999,
    placeholder: 'clpAndCnpTests.reiSupine',
    unit: <FormattedMessage id="clpAndCnpTests.perHour" />,
  },
  {
    type: 'NumberField',
    name: 'odi3',
    title: 'clpAndCnpTests.odi3',
    titleDescription: <FormattedMessage id="clpAndCnpTests.odi3Description" />,
    precision: 2,
    min: 0,
    max: 999999,
    placeholder: 'clpAndCnpTests.odi3',
    unit: <FormattedMessage id="clpAndCnpTests.perHour" />,
  },
  {
    type: 'NumberField',
    name: 'cai',
    title: 'clpAndCnpTests.cai',
    titleDescription: <FormattedMessage id="clpAndCnpTests.caiDescription" />,
    precision: 2,
    min: 0,
    max: 999999,
    placeholder: 'clpAndCnpTests.cai',
    unit: <FormattedMessage id="clpAndCnpTests.perHour" />,
  },
  {
    type: 'NumberField',
    name: 'averageSpO2',
    title: 'clpAndCnpTests.averageSpO2',
    titleDescription: <FormattedMessage id="clpAndCnpTests.averageSpO2Description" />,
    precision: 2,
    min: 0,
    max: 100,
    placeholder: 'clpAndCnpTests.averageSpO2',
    unit: '%',
  },
  {
    type: 'NumberField',
    name: 'spO2Below90',
    title: 'clpAndCnpTests.spO2Below90',
    precision: 2,
    min: 0,
    max: 100,
    placeholder: 'clpAndCnpTests.spO2Below90',
    unit: <FormattedMessage id="clpAndCnpTests.percentsOfNight" />,
  },
  {
    type: 'NumberField',
    name: 'lowestSpO2',
    title: 'clpAndCnpTests.lowestSpO2',
    precision: 2,
    min: 0,
    max: 100,
    placeholder: 'clpAndCnpTests.lowestSpO2',
    unit: '%',
  },
  {
    type: 'NumberField',
    name: 'respiratoryRate',
    title: 'clpAndCnpTests.respiratoryRate',
    precision: 2,
    min: 0,
    max: 1000,
    placeholder: 'clpAndCnpTests.respiratoryRate',
    unit: <FormattedMessage id="clpAndCnpTests.perMinute" />,
  },
  {
    type: 'NumberField',
    name: 'tcCo2Max',
    title: 'clpAndCnpTests.tcCO2max',
    precision: 3,
    unit: 'kPa',
    placeholder: 'clpAndCnpTests.tcCO2max',
  },
  {
    type: 'NumberField',
    name: 'tcCo2Min',
    title: 'clpAndCnpTests.tcCO2min',
    precision: 3,
    unit: 'kPa',
    placeholder: 'clpAndCnpTests.tcCO2min',
  },
  {
    type: 'Radio',
    name: 'incidenceOfLongTermStenosis',
    title: 'clpAndCnpTests.incidenceOfLongTermStenosis',
    options: ['yes', 'no', 'unknown'],
    optFormatter: (name: string | number): React.JSX.Element => (
      <FormattedMessage id={`clpAndCnpTests.opts.sleepPolygraphy.${name}`} />
    ),
  },
  {
    type: 'NumberField',
    name: 'flowRestrictionPercentage',
    title: 'clpAndCnpTests.flowRestrictionPercentage',
    min: 0,
    max: 100,
    unit: '%',
    precision: 1,
    placeholder: 'clpAndCnpTests.flowRestrictionPercentage',
  },
  {
    type: 'Radio',
    name: 'flowRestriction',
    title: 'clpAndCnpTests.flowRestriction',
    options: ['yes', 'no'],
    optFormatter: (name: string | number): React.JSX.Element => <FormattedMessage id={`general.${name}`} />,
  },
  {
    type: 'NumberField',
    name: 'plmi',
    title: 'clpAndCnpTests.plmi',
    titleDescription: <FormattedMessage id="clpAndCnpTests.plmiDescription" />,
    precision: 2,
    min: 0,
    max: 200,
    placeholder: 'clpAndCnpTests.plmi',
    unit: <FormattedMessage id="clpAndCnpTests.perHour" />,
  },
  {
    type: 'NumberField',
    name: 'plm',
    title: 'clpAndCnpTests.plm',
    precision: 2,
    min: 0,
    max: 100,
    placeholder: 'clpAndCnpTests.plm',
    unit: '%',
  },
];

const SleepPolygraphyForm = ({ formData, documents, viewing }: IOwnProps): React.JSX.Element => {
  const madTherapyDocs = filterByDocumentType(documents, 'madTherapy');
  const onGoingMADTherapies = madTherapyDocs.filter(m => !isEnded(m.hasEnded, m.endDate));
  const madTherapyOngoing = onGoingMADTherapies.length > 0;
  return (
    <React.Fragment>
      {fieldConfig.map(item => {
        // Only show this question if madTherapy is ongoing
        if (item.name === 'madTherapyInUse' && !madTherapyOngoing) return null;
        if (item.name === 'flowRestriction' && !formData.document.flowRestriction) return null;
        return (
          <FormRow key={item.name} title={item.title} description={item.titleDescription}>
            {item.type === 'PartialDate' ? (
              <InputHandler
                type={item.type}
                editing={!viewing}
                name={item.name}
                formData={formData}
                dateDefault={item.dateDefault ?? 'now'}
                isNotCancellable={item.isNotCancellable}
              />
            ) : item.type === 'NumberField' ? (
              <>
                <Unit unit={item.unit ?? ''}>
                  <InputHandler
                    type={item.type}
                    editing={!viewing}
                    name={item.name}
                    formData={formData}
                    precision={item.precision}
                    min={item.min}
                    max={item.max}
                    placeholder={item.placeholder}
                  />
                </Unit>
              </>
            ) : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            item.type === 'Radio' ? (
              <InputHandler
                type="Radio"
                editing={!viewing}
                name={item.name}
                formData={formData}
                options={item.options}
                optionFormatter={item.optFormatter}
              />
            ) : null}
          </FormRow>
        );
      })}
    </React.Fragment>
  );
};

interface IOwnProps {
  formData: IFormData<IDocumentWithTypeV2<'sleepPolygraphy'>>;
  viewing?: boolean;
  documents: Array<IDocumentWithTypeV2<TRelevantDocumentType>>;
}

export default SleepPolygraphyForm;
